import { type GetBoardDragItem, type ItemToRearrange, type RearrangableItem } from '~/components/Shared/Drag/dragTypes';

/**
 * Calculates the minimum distance from a point to a rectangle.
 * Returns 0 if the point is inside the rectangle.
 */
export const distanceToRect = (point: { x: number; y: number }, rect: DOMRect): number => {
  const dx = Math.max(rect.left - point.x, 0, point.x - rect.right);
  const dy = Math.max(rect.top - point.y, 0, point.y - rect.bottom);

  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Checks if a point is within the bounds of a rectangle (inclusive).
 */
export const pointWithinRect = (point: { x: number; y: number }, rect: DOMRect): boolean =>
  point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;

export const getHoverIndex = (adjacentItem: RearrangableItem) =>
  adjacentItem.direction === 'after' ? adjacentItem.index + 1 : adjacentItem.index;

export const calculateRearrangeData = (
  items: { id: string }[],
  draggedItem: ItemToRearrange,
  adjacentItem: RearrangableItem,
) => {
  const hoverIndex = getHoverIndex(adjacentItem);
  const draggingRight = hoverIndex > draggedItem.index;

  const newIndex = draggingRight ? hoverIndex - 1 : hoverIndex;

  const nextItemId = items[hoverIndex - 1]?.id;
  const previousItemId = items[hoverIndex]?.id;

  return { newIndex, nextItemId, previousItemId };
};

/** Use the dragged item's index in `items` when present (cross-surface drags keep source index). */
export const getRearrangeDragItem = (items: { id: string }[], draggedItem: GetBoardDragItem) => {
  const destinationIndex = items.findIndex((item) => item.id === draggedItem.firstItem.id);

  if (destinationIndex === -1) {
    return draggedItem;
  }

  return { ...draggedItem, index: destinationIndex };
};
